import { useState } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { GraftonChat } from "@/components/GraftonChat";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import {
  Plus, Play, GitBranch, Zap, Target,
  ArrowRight, CheckCircle2, Bot,
} from "lucide-react";

import type { Campaign, Scenario } from "@/components/sales-funnel/types";
import {
  CHANNELS, PIPELINE_STAGES,
  STAGE_LABELS, STAGE_ICONS,
} from "@/components/sales-funnel/constants";
import { DraggableCampaignCard } from "@/components/sales-funnel/DraggableCampaignCard";
import { ChannelDropZone } from "@/components/sales-funnel/ChannelDropZone";
import { ChannelStats } from "@/components/sales-funnel/ChannelStats";
import { CreateCampaignDialog } from "@/components/sales-funnel/CreateCampaignDialog";
import { AgentsTab } from "@/components/sales-funnel/AgentsTab";
import { useSalesFunnel } from "@/hooks/useSalesFunnel";
import {
  Eye, MousePointer, UserCheck, Crown,
} from "lucide-react";

const FUNNEL_ICONS = [Eye, MousePointer, UserCheck, Crown];
const FUNNEL_COLORS = ["hsl(var(--primary))", "hsl(220 70% 55%)", "hsl(38 90% 55%)", "hsl(150 60% 45%)"];
const FUNNEL_LABELS = ["Visiteurs", "Leads", "Prospects", "Clients"];

export default function SalesFunnelDashboard() {
  const {
    campaigns, scenarios, stats, agents, loading,
    createCampaign, updateCampaign, deleteCampaign,
    createScenario, updateScenario, refresh,
  } = useSalesFunnel();

  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [showNewScenario, setShowNewScenario] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);
  const [newScenarioName, setNewScenarioName] = useState("");
  const [newScenarioCampaign, setNewScenarioCampaign] = useState("");
  const [closingLoading, setClosingLoading] = useState(false);
  const [closingResults, setClosingResults] = useState<any>(null);
  const [closingHistory, setClosingHistory] = useState<any[]>([]);

  const handleAutoRelance = async () => {
    setClosingLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pipeline/auto-relance`, {
        method: "POST", credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setClosingResults(data);
        toast.success(`${data.sent} email(s) de relance envoyé(s)`);
        fetchClosingHistory();
      } else {
        toast.error("Erreur pipeline closing");
      }
    } catch { toast.error("Erreur réseau"); }
    setClosingLoading(false);
  };

  const fetchClosingHistory = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pipeline/history`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setClosingHistory(data.logs || []);
      }
    } catch {}
  };

  // Load history on mount
  useState(() => { fetchClosingHistory(); });

  const handleDrop = (campaignId: string, channelId: string) => {
    updateCampaign(campaignId, { channel: channelId });
    const ch = CHANNELS.find((c) => c.id === channelId);
    toast.success(`Campagne déplacée vers ${ch?.name}`);
  };

  const handleDelete = (id: string) => {
    deleteCampaign(id);
  };

  const handleCreateCampaign = (campaign: Campaign) => {
    const { id, ...rest } = campaign;
    createCampaign(rest as Omit<Campaign, "id">);
  };

  const handleExecute = (id: string) => {
    updateCampaign(id, { status: "active", stage: "active" });
    toast.success("Campagne lancée !");
  };

  const handleCreateScenario = () => {
    if (!newScenarioName || !newScenarioCampaign) return;
    createScenario({
      name: newScenarioName,
      campaignId: newScenarioCampaign,
      steps: [
        { id: crypto.randomUUID(), label: "Définir l'audience cible", type: "action", completed: false },
        { id: crypto.randomUUID(), label: "Attendre validation", type: "wait", completed: false },
        { id: crypto.randomUUID(), label: "Lancer la diffusion", type: "action", completed: false },
      ],
    });
    setNewScenarioName("");
    setNewScenarioCampaign("");
    setShowNewScenario(false);
  };

  const toggleStep = (scenarioId: string, stepId: string) => {
    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario) return;
    const updatedSteps = scenario.steps.map((st) =>
      st.id === stepId ? { ...st, completed: !st.completed } : st
    );
    updateScenario(scenarioId, { steps: updatedSteps });
  };

  // Build dynamic funnel data
  const funnelData = stats?.funnel
    ? [
        { key: "visitors", value: stats.funnel.visitors },
        { key: "leads", value: stats.funnel.leads },
        { key: "prospects", value: stats.funnel.prospects },
        { key: "clients", value: stats.funnel.clients },
      ]
    : [
        { key: "visitors", value: 0 },
        { key: "leads", value: 0 },
        { key: "prospects", value: 0 },
        { key: "clients", value: 0 },
      ];

  if (loading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-background">
          <DashboardSidebar />
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 overflow-y-auto">
          {/* Header */}
          <div className="border-b border-border/50 bg-card/30 backdrop-blur px-4 md:px-6 py-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary shrink-0" />
                  <span>Tunnel de Vente</span>
                </h1>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Construisez, pilotez et automatisez vos campagnes d'acquisition
                </p>
              </div>
              <div className="flex gap-2 flex-wrap shrink-0">
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setShowNewCampaign(true)} data-testid="new-campaign-btn">
                  <Plus className="h-3.5 w-3.5" /> Nouvelle campagne
                </Button>

                <Dialog open={showNewScenario} onOpenChange={setShowNewScenario}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="gap-1.5" data-testid="new-scenario-btn">
                      <GitBranch className="h-3.5 w-3.5" /> Scénario
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Créer un scénario d'automatisation</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                      <Input placeholder="Nom du scénario" value={newScenarioName} onChange={(e) => setNewScenarioName(e.target.value)} data-testid="scenario-name-input" />
                      <Select value={newScenarioCampaign} onValueChange={setNewScenarioCampaign}>
                        <SelectTrigger><SelectValue placeholder="Campagne associée" /></SelectTrigger>
                        <SelectContent>
                          {campaigns.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button onClick={handleCreateScenario} className="w-full" data-testid="create-scenario-submit">Créer le scénario</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>

          <div className="p-6">
            <Tabs defaultValue="campaigns" className="space-y-5">
              <TabsList className="bg-muted/50 backdrop-blur">
                <TabsTrigger value="campaigns" className="gap-1.5 text-xs" data-testid="tab-campaigns">
                  <Target className="h-3.5 w-3.5" /> Campagnes
                </TabsTrigger>
                <TabsTrigger value="closing" className="gap-1.5 text-xs" data-testid="tab-closing">
                  <Zap className="h-3.5 w-3.5" /> Closing
                </TabsTrigger>
                <TabsTrigger value="agents" className="gap-1.5 text-xs" data-testid="tab-agents">
                  <Bot className="h-3.5 w-3.5" /> Agents
                </TabsTrigger>
                <TabsTrigger value="scenarios" className="gap-1.5 text-xs" data-testid="tab-scenarios">
                  <GitBranch className="h-3.5 w-3.5" /> Scénarios
                </TabsTrigger>
              </TabsList>

              {/* TAB: Campagnes */}
              <TabsContent value="campaigns" className="space-y-6">
                {/* Dynamic Funnel */}
                <Card className="p-5 bg-card/50 backdrop-blur border-border/30">
                  <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Zap className="h-4 w-4 text-primary" /> Entonnoir de conversion
                    {stats?.nanocorp && (
                      <Badge variant="outline" className="text-[9px] ml-2">
                        {stats.nanocorp.total_missions} missions NanoCorp
                      </Badge>
                    )}
                  </h2>
                  <div className="flex items-center justify-center gap-2">
                    {funnelData.map((stage, i) => {
                      const Icon = FUNNEL_ICONS[i];
                      const widthPercent = 100 - i * 18;
                      const convRate = i > 0 && funnelData[i - 1].value > 0
                        ? ((stage.value / funnelData[i - 1].value) * 100).toFixed(1)
                        : null;
                      return (
                        <div key={stage.key} className="flex items-center gap-2">
                          <motion.div
                            initial={{ scaleX: 0, opacity: 0 }}
                            animate={{ scaleX: 1, opacity: 1 }}
                            transition={{ delay: i * 0.15, duration: 0.5, ease: "easeOut" }}
                            className="relative rounded-xl px-4 py-3 text-center"
                            style={{
                              background: FUNNEL_COLORS[i] + "18",
                              border: `1px solid ${FUNNEL_COLORS[i]}33`,
                              minWidth: `${widthPercent * 1.6}px`,
                            }}
                          >
                            <Icon className="h-5 w-5 mx-auto mb-1" style={{ color: FUNNEL_COLORS[i] }} />
                            <div className="text-lg font-bold" style={{ color: FUNNEL_COLORS[i] }}>
                              {stage.value.toLocaleString("fr-FR")}
                            </div>
                            <div className="text-[10px] text-muted-foreground">{FUNNEL_LABELS[i]}</div>
                          </motion.div>
                          {i < funnelData.length - 1 && (
                            <div className="flex flex-col items-center">
                              <ArrowRight className="h-4 w-4 text-muted-foreground/40" />
                              {convRate && (
                                <span className="text-[9px] text-muted-foreground/60 mt-0.5">{convRate}%</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>

                {/* Channel Stats */}
                <ChannelStats campaigns={campaigns} />

                {/* Channels + Pipeline */}
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-3 space-y-3">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                      Canaux d'acquisition
                    </h3>
                    <p className="text-[10px] text-muted-foreground/60 mb-3">
                      Glissez une campagne sur un canal pour la réassigner
                    </p>
                    {CHANNELS.map((ch) => (
                      <ChannelDropZone
                        key={ch.id}
                        channel={ch}
                        campaigns={campaigns.filter((c) => c.channel === ch.id)}
                        isHovered={false}
                      />
                    ))}
                  </div>
                  <div className="col-span-9">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                      Pipeline des campagnes
                    </h3>
                    {campaigns.length === 0 ? (
                      <Card className="p-8 bg-card/50 border-border/30 text-center">
                        <Target className="h-8 w-8 mx-auto text-muted-foreground/30 mb-3" />
                        <p className="text-sm text-muted-foreground mb-3">Aucune campagne. Créez votre première campagne pour commencer.</p>
                        <Button size="sm" onClick={() => setShowNewCampaign(true)} className="gap-1.5" data-testid="empty-new-campaign-btn">
                          <Plus className="h-3.5 w-3.5" /> Nouvelle campagne
                        </Button>
                      </Card>
                    ) : (
                      <div className="grid grid-cols-4 gap-3">
                        {PIPELINE_STAGES.map((stage) => {
                          const SIcon = STAGE_ICONS[stage];
                          const stageCampaigns = campaigns.filter((c) => c.status === stage);
                          return (
                            <div key={stage} className="space-y-2">
                              <div className="flex items-center gap-1.5 mb-2">
                                <SIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="text-xs font-medium">{STAGE_LABELS[stage]}</span>
                                <Badge variant="outline" className="ml-auto text-[9px] px-1.5 py-0">
                                  {stageCampaigns.length}
                                </Badge>
                              </div>
                              <div className="space-y-2 min-h-[100px]">
                                <AnimatePresence>
                                  {stageCampaigns.map((campaign) => (
                                    <motion.div
                                      key={campaign.id}
                                      layout
                                      initial={{ opacity: 0, y: 10 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      exit={{ opacity: 0, scale: 0.9 }}
                                    >
                                      <DraggableCampaignCard
                                        campaign={campaign}
                                        onDrop={handleDrop}
                                        onDelete={handleDelete}
                                      />
                                      {campaign.status === "draft" && (
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="w-full mt-1 text-[10px] h-6 gap-1 text-emerald-400 hover:text-emerald-300"
                                          onClick={() => handleExecute(campaign.id)}
                                          data-testid={`launch-campaign-${campaign.id}`}
                                        >
                                          <Play className="h-3 w-3" /> Lancer
                                        </Button>
                                      )}
                                    </motion.div>
                                  ))}
                                </AnimatePresence>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* TAB: Closing Pipeline */}
              <TabsContent value="closing" className="space-y-5">
                <Card className="p-5 bg-card/50 backdrop-blur border-border/30">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-sm font-semibold flex items-center gap-2">
                        <Zap className="h-4 w-4 text-primary" /> Pipeline Closing Automatique
                      </h2>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Grafton IA génère et envoie des emails de relance personnalisés aux prospects Axonaut
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="gap-1.5"
                      onClick={handleAutoRelance}
                      disabled={closingLoading}
                      data-testid="auto-relance-btn"
                    >
                      {closingLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                      Lancer les relances
                    </Button>
                  </div>

                  {/* Results */}
                  {closingResults && (
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-xs">
                        <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        <span className="font-medium">{closingResults.sent} email(s) envoyé(s) sur {closingResults.total} prospect(s)</span>
                      </div>
                      {closingResults.results?.map((r: any, i: number) => (
                        <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${
                          r.status === "sent" ? "bg-emerald-500/5 border-emerald-500/15" : "bg-red-500/5 border-red-500/15"
                        }`}>
                          <div className={`h-2 w-2 rounded-full ${r.status === "sent" ? "bg-emerald-500" : "bg-red-500"}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{r.prospect} — {r.contact}</p>
                            <p className="text-[10px] text-muted-foreground">{r.email} {r.subject ? `· ${r.subject}` : ""}</p>
                          </div>
                          <span className={`text-[10px] font-medium ${r.status === "sent" ? "text-emerald-400" : "text-red-400"}`}>
                            {r.status === "sent" ? "Envoyé" : "Erreur"}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* History */}
                  {closingHistory.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                        Historique des relances
                      </h3>
                      <div className="space-y-2">
                        {closingHistory.map((log, i) => (
                          <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-muted/20 hover:bg-muted/30 transition-colors">
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{log.action}</p>
                              <p className="text-[9px] text-muted-foreground">{log.details?.subject || ""}</p>
                            </div>
                            <span className="text-[9px] text-muted-foreground whitespace-nowrap">{log.created_at ? new Date(log.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {!closingResults && closingHistory.length === 0 && (
                    <div className="text-center py-8">
                      <Zap className="h-8 w-8 mx-auto text-muted-foreground/20 mb-3" />
                      <p className="text-sm text-muted-foreground mb-1">Aucune relance envoyée</p>
                      <p className="text-xs text-muted-foreground/60">Cliquez sur "Lancer les relances" pour envoyer des emails personnalisés à vos prospects Axonaut.</p>
                    </div>
                  )}
                </Card>
              </TabsContent>

              {/* TAB: Agents NanoCorp */}
              <TabsContent value="agents">
                <AgentsTab agents={agents} loading={loading} />
              </TabsContent>

              {/* TAB: Scénarios */}
              <TabsContent value="scenarios">
                <Card className="p-5 bg-card/50 backdrop-blur border-border/30">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-sm font-semibold flex items-center gap-2">
                      <GitBranch className="h-4 w-4 text-primary" /> Scénarios d'automatisation
                    </h2>
                  </div>
                  {scenarios.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-8">
                      Aucun scénario. Créez-en un pour automatiser vos campagnes.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {scenarios.map((scenario) => {
                        const linkedCampaign = campaigns.find((c) => c.id === scenario.campaignId);
                        const completedSteps = scenario.steps.filter((s: any) => s.completed).length;
                        const progress = scenario.steps.length > 0 ? (completedSteps / scenario.steps.length) * 100 : 0;
                        return (
                          <motion.div
                            key={scenario.id}
                            className="border border-border/40 rounded-lg p-4 hover:border-primary/20 transition-colors cursor-pointer"
                            onClick={() => setSelectedScenario(selectedScenario?.id === scenario.id ? null : scenario)}
                            whileHover={{ scale: 1.005 }}
                            data-testid={`scenario-${scenario.id}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Zap className="h-4 w-4 text-primary" />
                                <span className="text-sm font-medium">{scenario.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {linkedCampaign && (
                                  <Badge variant="outline" className="text-[9px]">
                                    {linkedCampaign.name}
                                  </Badge>
                                )}
                                <span className="text-[10px] text-muted-foreground">
                                  {completedSteps}/{scenario.steps.length} étapes
                                </span>
                              </div>
                            </div>
                            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                              <motion.div
                                className="h-full rounded-full bg-primary"
                                initial={{ width: 0 }}
                                animate={{ width: `${progress}%` }}
                                transition={{ duration: 0.6 }}
                              />
                            </div>

                            <AnimatePresence>
                              {selectedScenario?.id === scenario.id && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  className="overflow-hidden"
                                >
                                  <div className="pt-4 space-y-2">
                                    {scenario.steps.map((step: any, i: number) => (
                                      <motion.div
                                        key={step.id}
                                        initial={{ x: -10, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ delay: i * 0.08 }}
                                        className="flex items-center gap-3"
                                      >
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleStep(scenario.id, step.id);
                                          }}
                                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                            step.completed
                                              ? "bg-primary border-primary"
                                              : "border-muted-foreground/30"
                                          }`}
                                        >
                                          {step.completed && <CheckCircle2 className="h-3 w-3 text-primary-foreground" />}
                                        </button>
                                        <div className={`flex-1 text-xs ${step.completed ? "line-through text-muted-foreground/50" : ""}`}>
                                          {step.label}
                                        </div>
                                        <Badge variant="outline" className="text-[8px] px-1.5 py-0">
                                          {step.type === "action" ? "Action" : step.type === "wait" ? "Attente" : "Condition"}
                                        </Badge>
                                      </motion.div>
                                    ))}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </main>

        {/* Create Campaign Dialog */}
        <CreateCampaignDialog
          open={showNewCampaign}
          onOpenChange={setShowNewCampaign}
          onCreateCampaign={handleCreateCampaign}
        />

        {/* Grafton Chat */}
        <GraftonChat
          variant="funnel"
          context={{
            totalCampaigns: campaigns.length,
            activeCampaigns: campaigns.filter(c => c.status === "active").length,
            totalBudget: campaigns.reduce((s, c) => s + c.budget, 0),
            totalSpent: campaigns.reduce((s, c) => s + c.spent, 0),
            totalConversions: campaigns.reduce((s, c) => s + c.conversions, 0),
          }}
          quickPrompts={[
            "Optimise mes campagnes actives",
            "Quel canal a le meilleur ROI ?",
            "Propose un scénario d'acquisition B2B",
            "Comment réduire mon CPC ?",
          ]}
          subtitle="Expert acquisition"
          welcomeMessage="Demandez-moi des conseils pour optimiser vos campagnes et votre tunnel de vente."
        />
      </div>
    </SidebarProvider>
  );
}
